import { dateToLocale } from '../../utils-to-migrate';
import {
  getV1AppAgreements,
  ViewmodelAgreementPreview,
  ViewmodelAgreementStatus,
  ViewmodelAgreementInstallment,
  ViewmodelAgreement,
  getV1AppAgreementsByAgreementCode,
  deleteV1AppAgreementsByAgreementCode,
  getV1AppAgreementsTermsbyidByAgreementId,
} from '@acordocertobr/reneg-offers-hub';
import apiService from '../api/api.service';
import { FilterAgreementsParams, FilterInstallmentsParams, GetDerivedInstallmenStatusParams, InstallmentDerivedStatus } from './agreements.types';

// Dev mock: set VITE_USE_AGREEMENTS_MOCK=true in .env.local to bypass real API
// Swap mockAgreementsComplete for any other scenario from ../../_mocks/agreements/index
import { resolveMock } from '../../_mocks/utils';
import { mockAgreementsComplete, mockAgreementDetail } from '../../_mocks/agreements/index';

/**
 * Fetches agreements list from API
 */
export const getAgreementsList = (): Promise<ViewmodelAgreementPreview[]> => {
  if (import.meta.env.VITE_USE_AGREEMENTS_MOCK === 'true') {
    return resolveMock(mockAgreementsComplete);
  }
  return getV1AppAgreements({ client: apiService }).then((a) => a?.data?.agreements || []);
};

export const getAgreementsTerm = (agreementId: string) => {
  return getV1AppAgreementsTermsbyidByAgreementId({ client: apiService, path: { agreement_id: agreementId } });
};

/**
 * Fetches a specific agreement by its code
 * @param agreementCode - The agreement code to fetch
 * @returns Promise resolving to the agreement details
 */
export const getAgreement = (agreementCode: string): Promise<ViewmodelAgreement> => {
  if (import.meta.env.VITE_USE_AGREEMENTS_MOCK === 'true') {
    return resolveMock(mockAgreementDetail);
  }
  return getV1AppAgreementsByAgreementCode({ client: apiService, path: { agreement_code: agreementCode } }).then((a) => a.data!);
};

/**
 * Cancels an agreement by its code
 * @param agreementCode - The agreement code to cancel
 * @returns Promise for the cancellation operation
 */
export const cancelAgreement = (agreementCode: string) => {
  return deleteV1AppAgreementsByAgreementCode({ client: apiService, path: { agreement_code: agreementCode } });
};

/**
 * Filters agreements by search text
 * Searches by creditor name, contract, creation date or ID
 */
export const filterAgreementsByText = (term: string, agreements: ViewmodelAgreementPreview[]): ViewmodelAgreementPreview[] => {
  const lowerSearch = term.toLowerCase();
  return agreements.filter(
    (ag) =>
      ag.creditor.name.toLowerCase().includes(lowerSearch) ||
      dateToLocale(ag.createdAt).includes(lowerSearch) ||
      String(ag.id).includes(lowerSearch) ||
      ag.contract.includes(lowerSearch)
  );
};

/**
 * Filters agreements by status
 * Returns all agreements if status is 'ALL'
 */
export const filterAgreementsByStatus = (
  status: ViewmodelAgreementStatus | 'ALL',
  agreements: ViewmodelAgreementPreview[]
): ViewmodelAgreementPreview[] => {
  if (status === 'ALL') return agreements;
  return agreements.filter((agreement) => agreement.status === status);
};

/**
 * Applies combined filters of status and search text to agreements
 */
export const filterAgreements = ({ agreements, status, searchTerm }: FilterAgreementsParams) => {
  let newAgreements = agreements;
  if (status && status !== 'ALL') newAgreements = filterAgreementsByStatus(status, newAgreements);
  if (searchTerm) newAgreements = filterAgreementsByText(searchTerm, newAgreements);

  return newAgreements;
};

/**
 * Checks if an installment is expired
 */
export const getIsInstallmentExpired = (installment?: ViewmodelAgreementInstallment) => {
  if (!installment) return false;
  return installment.status === 'EXPIRED';
};

/**
 * Checks if there is a previous unpaid expired installment
 */
export const getHasPreviousInstallmentUnpaid = (agreement: ViewmodelAgreementPreview) => {
  const currentInstallment = agreement.currentMonthInstallment;
  const firstUnpaidInstallment = agreement.firstUnpaidInstallment;
  if (!currentInstallment || !firstUnpaidInstallment) return false;
  const unpaidInstallmentIsPrevious = firstUnpaidInstallment.number < currentInstallment.number;

  return unpaidInstallmentIsPrevious && getIsInstallmentExpired(firstUnpaidInstallment);
};

/**
 * Finds active agreements that have current month installment
 */
export const getHasAgreementInExpiration = (agreements: ViewmodelAgreementPreview[]) => {
  return agreements.some((agreement) => getIsInstallmentExpired(agreement.currentMonthInstallment));
};

/**
 * Returns agreements with installments available for current month payment
 */
export const getAgreementsWithCurrentMonthInstallment = (agreements: ViewmodelAgreementPreview[]) => {
  return agreements.filter((agreement) => {
    const currentMonthInstallment = agreement?.currentMonthInstallment;
    const currentInstallmentIsAvailable = currentMonthInstallment?.status === 'AVAILABLE';

    return currentInstallmentIsAvailable && !getHasPreviousInstallmentUnpaid(agreement);
  });
};

/**
 * Returns agreements with installments available for next month
 */
export const getAgreementsWithNextMonthInstallment = (agreements: ViewmodelAgreementPreview[]) => {
  return agreements.filter((agreement) => {
    const nextMonthInstallment = agreement?.nextMonthInstallment;
    const isCurrentInstallmentPaid = agreement?.currentMonthInstallment?.status === 'PAID';
    const nextInstallmentIsAvailable = nextMonthInstallment?.status === 'AVAILABLE';
    const nextInstallmentIsFirst = nextMonthInstallment?.number === 1;

    return nextInstallmentIsAvailable && (isCurrentInstallmentPaid || nextInstallmentIsFirst);
  });
};

/**
 * Filters agreements by status
 * Returns all agreements if status is 'ALL'
 */
export const filterInstallmentsByStatus = ({ status, installments, currentCategory, firstUnpaidInstallment }: FilterInstallmentsParams) => {
  return installments?.filter((inst) => {
    if (status !== 'ACTIVE') return true;

    const installment = inst.number !== firstUnpaidInstallment?.number;
    const isExternalInstallment = inst.paymentMethod === 'EXTERNAL';

    if (currentCategory === 'ALL') return installment;
    if (currentCategory === 'PAID') return installment && inst.status === 'PAID' && !isExternalInstallment;
    if (currentCategory === 'UNPAID') return (installment && inst.status !== 'PAID') || (isExternalInstallment && !inst?.isFirstUnpaid);

    return installment;
  });
};

/**
 * Derives the status flags for an installment based on its payment method and status
 * @param params - Object containing installment payment details
 * @param params.installmentPaymentMethod - The payment method of the installment
 * @param params.installmentPaymentStatus - The current payment status of the installment
 * @param params.billetCode - The billet code if available
 * @param params.pixCode - The PIX code if available
 * @param params.pixStatus - The PIX payment status if available
 * @returns Object containing boolean flags indicating various installment status conditions
 */
export const getDerivedInstallmentStatus = ({
  installmentPaymentMethod,
  installmentPaymentStatus,
  billetCode,
  pixCode,
  pixStatus,
}: GetDerivedInstallmenStatusParams): InstallmentDerivedStatus => {
  const isBilletPayment = installmentPaymentMethod === 'BILLET';
  const isPixPayment = installmentPaymentMethod === 'PIX';
  const isPixBilletHybridPayment = installmentPaymentMethod === 'PIX_BILLET';

  const isHybridCodesUnavailable = isPixBilletHybridPayment && !pixCode && !billetCode;
  const isBilletExclusiveUnavailable = isBilletPayment && !billetCode;
  const isPixExclusiveUnavailable = isPixPayment && !pixCode;

  return {
    // Payment methods
    isPixPayment,
    isBilletPayment,
    isPixBilletHybridPayment,
    isExternalPayment: installmentPaymentMethod === 'EXTERNAL',
    isDebitPayment: installmentPaymentMethod === 'DEBIT_AUTO',

    // Payment status
    isUnavailable:
      installmentPaymentStatus === 'UNAVAILABLE' || isHybridCodesUnavailable || isBilletExclusiveUnavailable || isPixExclusiveUnavailable,
    isPaymentInAnalysis:
      installmentPaymentStatus === 'ANALYSIS' || installmentPaymentStatus === 'PAID' || pixStatus === 'ANALYSIS' || pixStatus === 'PAID',
    isBilletExpired: installmentPaymentStatus === 'EXPIRED',
    isBilletUnavailable: (installmentPaymentMethod === 'BILLET' || installmentPaymentMethod === 'PIX_BILLET') && !billetCode,
    isPixUnavailable: (installmentPaymentMethod === 'PIX' || installmentPaymentMethod === 'PIX_BILLET') && !pixCode,
  };
};
